import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import IncomingLinksSampleQuery, {
	type preloadIncomingLinksSampleQuery$data,
	type preloadIncomingLinksSampleQuery$variables,
} from './__generated__/preloadIncomingLinksSampleQuery.graphql.ts';
import {
	buildIncomingLinksSampleVariables,
	normalizeIncomingLinksSsrState,
	type IncomingLinksSsrState,
} from './incomingLinksSsrState.ts';

// Passed only while the read is skipped, so the exact value never keys a real cache lookup.
const SKIPPED_VARIABLES: preloadIncomingLinksSampleQuery$variables = { cypherQuery: '', first: 0 };

// Reads the exact bounded sample the inbound preload warmed, from the AGG query cache, without ever
// issuing a network request (`cache-only`). Mirrors `useOutboundBylineSsrState`: state is derived
// purely from the hydrated cache, so it is identical on the server and the client's first render.
// A cache miss (no preload, invalid input, or a non-view route) produces no inbound SSR footprint.
export const useIncomingLinksSsrState = (
	contentId: string | undefined,
): IncomingLinksSsrState | undefined => {
	const { cloudId } = useSessionData();
	const variables =
		!contentId || !cloudId ? undefined : buildIncomingLinksSampleVariables(cloudId, contentId);

	const { data } = useQueryInterop<
		preloadIncomingLinksSampleQuery$data,
		preloadIncomingLinksSampleQuery$variables
	>(IncomingLinksSampleQuery, {
		variables: variables ?? SKIPPED_VARIABLES,
		fetchPolicy: 'cache-only',
		skip: variables === undefined,
		interopApolloAggClient: true,
	});

	// An undefined `variables` forces `skip`, which forces `data` undefined, so the `data` guard
	// already covers it; the `contentId` guard also narrows it to a string for the normalizer.
	if (!contentId || data === undefined) {
		return undefined;
	}

	return normalizeIncomingLinksSsrState(contentId, data);
};
